import { useState, useRef, useEffect } from 'react'
import { useUpdateGoal } from '../mutations/useUpdateGoal.js'
import { colorFor } from '../data/userColors.js'

const TEAM = [
  'Ricky', 'Zoe', 'Christina', 'Katie', 'Margo', 'Emily',
  'Sandra', 'Sergina', 'Jonathan', 'Chris', 'Harold',
  'Jessie', 'Christine', 'Braden', 'Colin', 'Jane',
]

/**
 * SubgoalOwnersPicker — compact multi-select for sub-goal owners.
 *
 * A sub-goal can have one owner, or several (e.g. a build that needs
 * both design + engineering). The value is stored as a comma-separated
 * string (e.g. "Katie, Zoe") to match the existing data format used by
 * goal-level owners.
 *
 * Display:
 *   - Empty: yellow "Unassigned" pill
 *   - One owner: a colored pill with their name (same look as the old
 *     single-select OwnerPicker, so existing sub-goals look unchanged)
 *   - Multiple owners: "First +N" pill in neutral gray — space-efficient
 *     in the narrow Owner column, full list visible on hover (title attr)
 *
 * Click → checkbox dropdown; toggle names to add/remove.
 */
export default function SubgoalOwnersPicker({
  pillarName,
  goalIndex,
  subgoalIndex,
  value,
}) {
  const [open, setOpen] = useState(false)
  const wrapRef = useRef(null)
  const mutation = useUpdateGoal()

  const owners = parseOwners(value)

  // Close on outside click + Escape
  useEffect(() => {
    if (!open) return
    const onClick = (e) => {
      if (!wrapRef.current) return
      if (!wrapRef.current.contains(e.target)) setOpen(false)
    }
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false)
    }
    const t = setTimeout(() => {
      document.addEventListener('click', onClick)
      document.addEventListener('keydown', onKey)
    }, 0)
    return () => {
      clearTimeout(t)
      document.removeEventListener('click', onClick)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  const toggle = (name) => {
    const exists = owners.includes(name)
    const next = exists ? owners.filter((o) => o !== name) : [...owners, name]
    const nextValue = next.length === 0 ? '—' : next.join(', ')
    mutation.mutate({
      pillarName,
      goalIndex,
      subgoalIndex,
      field: 'owner',
      value: nextValue,
    })
  }

  const openPicker = (e) => {
    e.stopPropagation()
    setOpen((x) => !x)
  }

  return (
    <div
      ref={wrapRef}
      style={{
        position: 'relative',
        display: 'flex',
        flexWrap: 'wrap',
        gap: 3,
        alignItems: 'center',
        minWidth: 0,
      }}
    >
      {owners.length === 0 && (
        <button
          type="button"
          onClick={openPicker}
          title="Assign owner(s)"
          style={{
            fontSize: 10,
            fontWeight: 700,
            color: '#92400e',
            background: '#fffbeb',
            border: '1px solid #fde68a',
            padding: '2px 8px',
            borderRadius: 99,
            cursor: 'pointer',
            whiteSpace: 'nowrap',
            userSelect: 'none',
          }}
        >
          TBD ▼
        </button>
      )}

      {owners.map((name) => {
        const c = colorFor(name)
        return (
          <button
            key={name}
            type="button"
            onClick={openPicker}
            title={`${name} — click to edit owners`}
            style={{
              fontSize: 10,
              fontWeight: 700,
              color: c,
              background: c + '18',
              border: `1px solid ${c}30`,
              padding: '2px 8px',
              borderRadius: 99,
              cursor: 'pointer',
              whiteSpace: 'nowrap',
              userSelect: 'none',
            }}
          >
            {name}
          </button>
        )
      })}

      {owners.length > 0 && (
        <button
          type="button"
          onClick={openPicker}
          title="Add / remove owners"
          style={{
            fontSize: 10,
            fontWeight: 700,
            color: 'var(--muted)',
            background: '#fff',
            border: '1px dashed var(--border)',
            padding: '1px 6px',
            borderRadius: 99,
            cursor: 'pointer',
            lineHeight: 1.4,
            userSelect: 'none',
          }}
        >
          ＋
        </button>
      )}

      {open && (
        <div
          style={{
            position: 'absolute',
            top: 'calc(100% + 4px)',
            left: 0,
            minWidth: 180,
            maxHeight: 300,
            overflowY: 'auto',
            background: '#fff',
            border: '1px solid var(--border)',
            borderRadius: 8,
            boxShadow: '0 8px 24px rgba(0,0,0,0.1)',
            padding: 4,
            zIndex: 950,
          }}
        >
          <div
            style={{
              fontSize: 9,
              fontWeight: 700,
              color: 'var(--muted)',
              textTransform: 'uppercase',
              letterSpacing: 0.06,
              padding: '4px 8px 6px',
            }}
          >
            Assign owners · {owners.length} selected
          </div>
          {TEAM.map((name) => {
            const c = colorFor(name)
            const isSelected = owners.includes(name)
            return (
              <button
                key={name}
                type="button"
                onClick={() => toggle(name)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  width: '100%',
                  border: 'none',
                  background: isSelected ? c + '12' : 'transparent',
                  padding: '6px 8px',
                  borderRadius: 6,
                  fontSize: 12,
                  fontWeight: isSelected ? 700 : 500,
                  color: 'var(--text)',
                  cursor: 'pointer',
                  textAlign: 'left',
                }}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.background = isSelected ? c + '1a' : '#f9fafb')
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.background = isSelected ? c + '12' : 'transparent')
                }
              >
                <span
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 16,
                    height: 16,
                    borderRadius: 4,
                    border: `1.5px solid ${isSelected ? c : '#d1d5db'}`,
                    background: isSelected ? c : '#fff',
                    color: '#fff',
                    fontSize: 10,
                    fontWeight: 800,
                    flexShrink: 0,
                  }}
                >
                  {isSelected ? '✓' : ''}
                </span>
                <span
                  style={{
                    display: 'inline-block',
                    width: 18,
                    height: 18,
                    borderRadius: '50%',
                    background: c,
                    color: '#fff',
                    fontSize: 10,
                    fontWeight: 800,
                    lineHeight: '18px',
                    textAlign: 'center',
                  }}
                >
                  {name[0]}
                </span>
                <span>{name}</span>
              </button>
            )
          })}
        </div>
      )}
    </div>
  )
}

function parseOwners(value) {
  return String(value || '')
    .split(',')
    .map((s) => s.trim())
    .filter((s) => s && s !== '—' && s !== 'TBD')
}
